import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { usePrototype } from './PrototypeContext';
import { seedApplications, seedComplaints, seedDelivery, seedNotifications, seedOwnerAudit, seedRegistrations, seedRequests } from '../data/ownerAdmin';
import { toBn } from '../utils/bn';
import type { ApplicationStatus, ChangeRequest, Complaint, ComplaintStatus, DeliveryLog, OwnerApplication, OwnerAuditEvent, OwnerNotification, OwnerRegistration, ProfileStatus, RequestStatus } from '../types/owner';
const OWNER_ID = 'OWN-2026-000125';
const CNG_STAGE_FOR: number[] = [0, 1, 1, 2, 3, 4, 5, 7];
type NewApplication = Omit<OwnerApplication, 'id' | 'stage' | 'status' | 'times' | 'submitted'>;
type NewRequest = Omit<ChangeRequest, 'id' | 'date' | 'status'>;
type NewComplaint = Omit<Complaint, 'id' | 'date' | 'status' | 'remarks'>;
interface OwnerServiceState {
  ownerId: string;
  applications: OwnerApplication[];
  myApplications: OwnerApplication[];
  submitApplication: (a: NewApplication) => string;
  patchApplication: (id: string, patch: Partial<OwnerApplication>) => void;
  advanceApplication: (id: string) => void;
  returnApplication: (id: string, reason: string) => void;
  rejectApplication: (id: string, reason: string) => void;
  resubmitApplication: (id: string) => void;
  registrations: OwnerRegistration[];
  myProfileStatus: ProfileStatus;
  setRegistrationStatus: (id: string, status: ProfileStatus, remark?: string) => void;
  submitProfile: () => void;
  requests: ChangeRequest[];
  submitRequest: (r: NewRequest) => string;
  setRequestStatus: (id: string, status: RequestStatus) => void;
  notifications: OwnerNotification[];
  notify: (n: Omit<OwnerNotification, 'id' | 'time' | 'read'>) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  complaints: Complaint[];
  submitComplaint: (c: NewComplaint) => string;
  updateComplaint: (id: string, patch: {
    status?: ComplaintStatus;
    assigned?: string;
    remark?: string;
  }) => void;
  delivery: DeliveryLog[];
  addDelivery: (d: Omit<DeliveryLog, 'id' | 'time'>) => void;
  audit: OwnerAuditEvent[];
  log: (e: Omit<OwnerAuditEvent, 'time'>) => void;
}
const OwnerServiceContext = createContext<OwnerServiceState | null>(null);
function nowStamp(): string {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return toBn(`13/10/2026 ${hh}:${mm}`);
}
export function OwnerServiceProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    setStage,
    assignDriver,
    removeDriver
  } = usePrototype();
  const [applications, setApplications] = useState<OwnerApplication[]>(seedApplications);
  const [registrations, setRegistrations] = useState<OwnerRegistration[]>(seedRegistrations);
  const [requests, setRequests] = useState<ChangeRequest[]>(seedRequests);
  const [notifications, setNotifications] = useState<OwnerNotification[]>(seedNotifications);
  const [complaints, setComplaints] = useState<Complaint[]>(seedComplaints);
  const [delivery, setDelivery] = useState<DeliveryLog[]>(seedDelivery);
  const [audit, setAudit] = useState<OwnerAuditEvent[]>(seedOwnerAudit);
  const counters = useRef({
    app: 129,
    req: 344,
    tkt: 215,
    n: 1,
    dl: 88214
  });
  const log = useCallback((e: Omit<OwnerAuditEvent, 'time'>) => setAudit(a => [{
    ...e,
    time: nowStamp()
  }, ...a]), []);
  const notify = useCallback((n: Omit<OwnerNotification, 'id' | 'time' | 'read'>) => {
    counters.current.n += 1;
    const id = `nx${counters.current.n}`;
    setNotifications(ns => [{
      ...n,
      id,
      time: nowStamp(),
      read: false
    }, ...ns]);
  }, []);
  const addDelivery = useCallback((d: Omit<DeliveryLog, 'id' | 'time'>) => {
    counters.current.dl += 1;
    const id = `DL-${counters.current.dl}`;
    setDelivery(ds => [{
      ...d,
      id,
      time: nowStamp()
    }, ...ds]);
  }, []);
  const isMine = (a: OwnerApplication) => a.ownerId === OWNER_ID;
  const submitApplication = useCallback((a: NewApplication) => {
    const id = `DMP-OWN-2026-${String(counters.current.app).padStart(6, '0')}`;
    counters.current.app += 1;
    setApplications(as => {
      const dup = as.find(x => x.vehicleReg === a.vehicleReg && x.status !== 'বাতিল');
      return [{
        ...a,
        id,
        stage: 1,
        status: 'অপেক্ষমাণ',
        submitted: '১৩/১০/২০২৬',
        times: [nowStamp()],
        duplicateOf: dup?.id
      }, ...as];
    });
    log({
      actor: a.ownerName,
      event: 'স্ব-যাচাই আবেদন জমা',
      record: `${id} • ${a.vehicleReg}`,
      channel: 'মালিক অ্যাপ',
      device: 'Android 14 • 103.98.12.44'
    });
    if (a.ownerId === OWNER_ID) {
      notify({
        cat: 'আবেদন আপডেট',
        title: 'আবেদন সফলভাবে জমা হয়েছে।',
        body: `আপনার গাড়ির আবেদন ${id} সফলভাবে জমা হয়েছে।`,
        important: false,
        action: 'tracking',
        vehicleId: a.vehicleId,
        appId: id
      });
    }
    return id;
  }, [log, notify]);
  const patchApp = (id: string, fn: (a: OwnerApplication) => OwnerApplication) => setApplications(as => as.map(a => a.id === id ? fn(a) : a));
  const patchApplication = useCallback((id: string, patch: Partial<OwnerApplication>) => {
    setApplications(as => as.map(a => a.id === id ? {
      ...a,
      ...patch
    } : a));
  }, []);
  const advanceApplication = useCallback((id: string) => {
    const app = applications.find(a => a.id === id);
    if (!app || app.stage >= 7) return;
    const stage = app.stage + 1;
    const status: ApplicationStatus = stage >= 7 ? 'স্টিকার ইস্যুকৃত' : stage >= 5 ? 'অনুমোদিত' : 'প্রক্রিয়াধীন';
    patchApp(id, a => ({
      ...a,
      stage,
      status,
      correctionReason: undefined,
      times: [...a.times.slice(0, stage - 1), nowStamp()]
    }));
    if (app.vehicleId) setStage(app.vehicleId, CNG_STAGE_FOR[stage]);
    const events = ['', '', 'তথ্য পর্যালোচনা', 'BRTA তথ্য যাচাই', 'কর্মকর্তা যাচাই', 'অনুমোদন', 'QR স্টিকার তৈরি', 'স্টিকার ইস্যু'];
    log({
      actor: 'মোঃ শফিকুল ইসলাম',
      event: events[stage],
      record: id,
      channel: 'ওয়েব পোর্টাল',
      device: 'Chrome • 10.20.4.17'
    });
    const isOwn = isMine(app);
    if (stage === 5) {
      if (isOwn) notify({
        cat: 'গাড়ির অনুমোদন',
        title: 'আপনার গাড়ির তথ্য অনুমোদিত হয়েছে।',
        body: `${app.vehicleReg} গাড়ির যাচাই সম্পন্ন হয়েছে। শীঘ্রই QR স্টিকার তৈরি করা হবে।`,
        important: false,
        action: 'tracking',
        vehicleId: app.vehicleId
      });
      addDelivery({
        template: 'অনুমোদন বার্তা',
        recipient: `${app.ownerName} (${app.ownerMobile})`,
        channel: 'অ্যাপ + SMS',
        status: 'পৌঁছেছে'
      });
    } else if (stage === 6) {
      if (isOwn) notify({
        cat: 'QR স্টিকার প্রস্তুত',
        title: 'QR স্টিকার সংগ্রহ করুন।',
        body: `${app.vehicleReg} গাড়ির QR স্টিকার প্রস্তুত রয়েছে। মূল NID ও রেজিস্ট্রেশন সার্টিফিকেটসহ বিতরণ কেন্দ্রে আসুন।`,
        important: true,
        action: 'qr',
        vehicleId: app.vehicleId
      });
      addDelivery({
        template: 'স্টিকার সংগ্রহ নির্দেশনা',
        recipient: `${app.ownerName} (${app.ownerMobile})`,
        channel: 'অ্যাপ + SMS',
        status: 'পৌঁছেছে'
      });
    } else if (stage === 7) {
      if (isOwn) notify({
        cat: 'স্টিকার সংগ্রহের নির্দেশনা',
        title: 'QR স্টিকার ইস্যু সম্পন্ন হয়েছে।',
        body: `${app.vehicleReg} গাড়িতে স্টিকারটি সামনের কাচের ভেতরের দিকে বাম কোণে স্থাপন করুন।`,
        important: false,
        action: 'qr',
        vehicleId: app.vehicleId
      });
    } else if (isOwn) {
      notify({
        cat: 'আবেদন আপডেট',
        title: 'আপনার আবেদন পরবর্তী ধাপে গেছে।',
        body: `${app.vehicleReg} — ${events[stage]} সম্পন্ন হয়েছে।`,
        important: false,
        action: 'tracking',
        vehicleId: app.vehicleId
      });
    }
  }, [applications, setStage, log, notify, addDelivery]);
  const returnApplication = useCallback((id: string, reason: string) => {
    const app = applications.find(a => a.id === id);
    if (!app) return;
    patchApp(id, a => ({
      ...a,
      status: 'সংশোধন প্রয়োজন',
      correctionReason: reason
    }));
    log({
      actor: 'মোঃ শফিকুল ইসলাম',
      event: 'সংশোধনে ফেরত',
      record: id,
      channel: 'ওয়েব পোর্টাল',
      device: 'Chrome • 10.20.4.17'
    });
    if (isMine(app)) notify({
      cat: 'তথ্য সংশোধনের অনুরোধ',
      title: 'আপনার আবেদনে সংশোধন প্রয়োজন।',
      body: `${app.vehicleReg} — ${reason}`,
      important: true,
      action: 'tracking',
      vehicleId: app.vehicleId
    });
    addDelivery({
      template: 'সংশোধনের অনুরোধ',
      recipient: `${app.ownerName} (${app.ownerMobile})`,
      channel: 'অ্যাপ + SMS',
      status: 'পৌঁছেছে'
    });
  }, [applications, log, notify, addDelivery]);
  const rejectApplication = useCallback((id: string, reason: string) => {
    const app = applications.find(a => a.id === id);
    if (!app) return;
    patchApp(id, a => ({
      ...a,
      status: 'বাতিল',
      correctionReason: reason
    }));
    log({
      actor: 'মোঃ শফিকুল ইসলাম',
      event: 'আবেদন বাতিল',
      record: id,
      channel: 'ওয়েব পোর্টাল',
      device: 'Chrome • 10.20.4.17'
    });
    if (isMine(app)) notify({
      cat: 'আবেদন আপডেট',
      title: 'আপনার আবেদন বাতিল করা হয়েছে।',
      body: `${app.vehicleReg} — ${reason}`,
      important: true,
      action: 'tracking',
      vehicleId: app.vehicleId
    });
  }, [applications, log, notify]);
  const resubmitApplication = useCallback((id: string) => {
    patchApp(id, a => ({
      ...a,
      status: 'প্রক্রিয়াধীন',
      correctionReason: undefined
    }));
    log({
      actor: 'মোঃ রহিম উদ্দিন',
      event: 'সংশোধিত তথ্য পুনরায় জমা',
      record: id,
      channel: 'মালিক অ্যাপ',
      device: 'Android 14 • 103.98.12.44'
    });
  }, [log]);
  const setRegistrationStatus = useCallback((id: string, status: ProfileStatus, remark?: string) => {
    setRegistrations(rs => rs.map(r => r.id === id ? {
      ...r,
      status,
      remark: remark ?? r.remark
    } : r));
    log({
      actor: 'মোঃ শফিকুল ইসলাম',
      event: `প্রোফাইল — ${status}`,
      record: id,
      channel: 'ওয়েব পোর্টাল',
      device: 'Chrome • 10.20.4.17'
    });
    if (id === OWNER_ID) {
      notify({
        cat: status === 'যাচাইকৃত' ? 'আবেদন আপডেট' : 'তথ্য সংশোধনের অনুরোধ',
        title: status === 'যাচাইকৃত' ? 'আপনার প্রোফাইল যাচাইকৃত হয়েছে।' : `প্রোফাইল: ${status}`,
        body: remark ?? 'ডিএমপি কর্তৃক আপনার ব্যক্তিগত তথ্য পর্যালোচনা সম্পন্ন হয়েছে।',
        important: status !== 'যাচাইকৃত',
        action: 'profileForm'
      });
    }
  }, [log, notify]);
  const submitProfile = useCallback(() => {
    setRegistrations(rs => rs.map(r => r.id === OWNER_ID ? {
      ...r,
      status: 'যাচাই অপেক্ষমাণ',
      registered: '১৩/১০/২০২৬',
      remark: undefined
    } : r));
    log({
      actor: 'মোঃ রহিম উদ্দিন',
      event: 'প্রোফাইল তথ্য জমা',
      record: OWNER_ID,
      channel: 'মালিক অ্যাপ',
      device: 'Android 14 • 103.98.12.44'
    });
  }, [log]);
  const submitRequest = useCallback((r: NewRequest) => {
    const id = `REQ-2026-${String(counters.current.req).padStart(5, '0')}`;
    counters.current.req += 1;
    setRequests(rs => [{
      ...r,
      id,
      date: '১৩/১০/২০২৬',
      status: 'অপেক্ষমাণ'
    }, ...rs]);
    log({
      actor: r.ownerName,
      event: `${r.kind} আবেদন`,
      record: `${id} • ${r.vehicleReg}`,
      channel: 'মালিক অ্যাপ',
      device: 'Android 14 • 103.98.12.44'
    });
    return id;
  }, [log]);
  const setRequestStatus = useCallback((id: string, status: RequestStatus) => {
    const req = requests.find(r => r.id === id);
    if (!req) return;
    setRequests(rs => rs.map(r => r.id === id ? {
      ...r,
      status
    } : r));
    log({
      actor: 'মোঃ শফিকুল ইসলাম',
      event: `${req.kind} — ${status}`,
      record: id,
      channel: 'ওয়েব পোর্টাল',
      device: 'Chrome • 10.20.4.17'
    });
    if (status === 'অনুমোদিত' && req.vehicleId && req.driverId) {
      if (req.kind === 'চালক সংযুক্তি') assignDriver(req.vehicleId, req.driverId);
      if (req.kind === 'চালক অপসারণ') removeDriver(req.vehicleId, req.driverId);
    }
    if (req.ownerName === 'মোঃ রহিম উদ্দিন' && (status === 'অনুমোদিত' || status === 'বাতিল')) {
      notify({
        cat: req.kind.startsWith('চালক') ? 'চালক অনুমোদন' : 'আবেদন আপডেট',
        title: `${req.kind} আবেদন ${status === 'অনুমোদিত' ? 'অনুমোদিত' : 'বাতিল'} হয়েছে।`,
        body: `${req.id} • ${req.vehicleReg} — ${req.proposed}`,
        important: false,
        action: req.kind.startsWith('চালক') ? 'drivers' : 'tracking',
        vehicleId: req.vehicleId
      });
    }
  }, [requests, log, assignDriver, removeDriver, notify]);
  const markRead = useCallback((id: string) => setNotifications(ns => ns.map(n => n.id === id ? {
    ...n,
    read: true
  } : n)), []);
  const markAllRead = useCallback(() => setNotifications(ns => ns.map(n => ({
    ...n,
    read: true
  }))), []);
  const submitComplaint = useCallback((c: NewComplaint) => {
    const id = `TKT-2026-${String(counters.current.tkt).padStart(5, '0')}`;
    counters.current.tkt += 1;
    setComplaints(cs => [{
      ...c,
      id,
      date: '১৩/১০/২০২৬',
      status: 'নতুন',
      remarks: []
    }, ...cs]);
    log({
      actor: c.ownerName,
      event: 'অভিযোগ দাখিল',
      record: id,
      channel: 'মালিক অ্যাপ',
      device: 'Android 14 • 103.98.12.44'
    });
    return id;
  }, [log]);
  const updateComplaint = useCallback((id: string, patch: {
    status?: ComplaintStatus;
    assigned?: string;
    remark?: string;
  }) => {
    setComplaints(cs => cs.map(c => c.id === id ? {
      ...c,
      status: patch.status ?? c.status,
      assigned: patch.assigned ?? c.assigned,
      remarks: patch.remark ? [...c.remarks, {
        by: 'মোঃ শফিকুল ইসলাম',
        text: patch.remark,
        time: nowStamp()
      }] : c.remarks
    } : c));
    log({
      actor: 'মোঃ শফিকুল ইসলাম',
      event: `অভিযোগ হালনাগাদ${patch.status ? ` — ${patch.status}` : ''}`,
      record: id,
      channel: 'ওয়েব পোর্টাল',
      device: 'Chrome • 10.20.4.17'
    });
  }, [log]);
  const myApplications = useMemo(() => applications.filter(isMine), [applications]);
  const myProfileStatus = registrations.find(r => r.id === OWNER_ID)?.status ?? 'যাচাই অপেক্ষমাণ';
  const value: OwnerServiceState = {
    ownerId: OWNER_ID,
    applications,
    myApplications,
    submitApplication,
    patchApplication,
    advanceApplication,
    returnApplication,
    rejectApplication,
    resubmitApplication,
    registrations,
    myProfileStatus,
    setRegistrationStatus,
    submitProfile,
    requests,
    submitRequest,
    setRequestStatus,
    notifications,
    notify,
    markRead,
    markAllRead,
    complaints,
    submitComplaint,
    updateComplaint,
    delivery,
    addDelivery,
    audit,
    log
  };
  return <OwnerServiceContext.Provider value={value} data-id="element-2243" data-mp="o0kuc860dp03">{children}</OwnerServiceContext.Provider>;
}
export function useOwnerService(): OwnerServiceState {
  const ctx = useContext(OwnerServiceContext);
  if (!ctx) throw new Error('useOwnerService must be used inside OwnerServiceProvider');
  return ctx;
}